import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ScienceIcon from '@mui/icons-material/Science';
import { fetchAuthSession } from '@aws-amplify/auth';
import config from '../utils/Constants';
import { generateTrialSubjects } from '../redux/userSlice';

const THERAPEUTIC_AREAS = [
  'Oncology',
  'Cardiovascular',
  'CNS',
  'Immunology',
  'Rare Disease',
  'Metabolic',
  'Infectious Disease',
  'Other',
];

const PHASES = ['Phase I', 'Phase II', 'Phase III', 'Phase IV'];

const ALLOCATIONS = [
  { value: 'RANDOMIZED', label: 'Randomized' },
  { value: 'NON_RANDOMIZED', label: 'Non-Randomized' },
];

const INTERVENTION_MODELS = [
  { value: 'PARALLEL', label: 'Parallel' },
  { value: 'CROSSOVER', label: 'Crossover' },
  { value: 'SINGLE_GROUP', label: 'Single Group' },
  { value: 'FACTORIAL', label: 'Factorial' },
  { value: 'SEQUENTIAL', label: 'Sequential' },
];

const MASKING_OPTIONS = [
  { value: 'NONE', label: 'None (Open Label)' },
  { value: 'SINGLE', label: 'Single' },
  { value: 'DOUBLE', label: 'Double' },
  { value: 'TRIPLE', label: 'Triple' },
  { value: 'QUADRUPLE', label: 'Quadruple' },
];

const PRIMARY_PURPOSES = [
  { value: 'TREATMENT', label: 'Treatment' },
  { value: 'PREVENTION', label: 'Prevention' },
  { value: 'DIAGNOSTIC', label: 'Diagnostic' },
  { value: 'SUPPORTIVE_CARE', label: 'Supportive Care' },
  { value: 'BASIC_SCIENCE', label: 'Basic Science' },
  { value: 'HEALTH_SERVICES_RESEARCH', label: 'Health Services Research' },
  { value: 'OTHER', label: 'Other' },
];

const INTERVENTION_TYPES = [
  { value: 'DRUG', label: 'Drug' },
  { value: 'DEVICE', label: 'Device' },
  { value: 'BIOLOGICAL', label: 'Biological' },
  { value: 'PROCEDURE', label: 'Procedure' },
  { value: 'BEHAVIORAL', label: 'Behavioral' },
  { value: 'DIETARY_SUPPLEMENT', label: 'Dietary Supplement' },
  { value: 'RADIATION', label: 'Radiation' },
  { value: 'OTHER', label: 'Other' },
];

const SEX_OPTIONS = [
  { value: 'ALL', label: 'All' },
  { value: 'MALE', label: 'Male Only' },
  { value: 'FEMALE', label: 'Female Only' },
];

const ARM_TYPES = [
  { value: 'EXPERIMENTAL', label: 'Experimental' },
  { value: 'PLACEBO_COMPARATOR', label: 'Placebo Comparator' },
  { value: 'ACTIVE_COMPARATOR', label: 'Active Comparator' },
  { value: 'SHAM_COMPARATOR', label: 'Sham Comparator' },
  { value: 'NO_INTERVENTION', label: 'No Intervention' },
  { value: 'OTHER', label: 'Other' },
];

const DISPOSITION_COLORS = {
  COMPLETED: 'success',
  'ADVERSE EVENT': 'error',
  'WITHDRAWAL BY SUBJECT': 'warning',
  'PROTOCOL DEVIATION': 'warning',
  OTHER: 'default',
};

const DEFAULT_ARMS = [
  { arm_id: 'TRT', label: 'Treatment', arm_type: 'EXPERIMENTAL' },
  { arm_id: 'PBO', label: 'Placebo', arm_type: 'PLACEBO_COMPARATOR' },
];

const AE_SEV_COLORS = { SEVERE: 'error', MODERATE: 'warning', MILD: 'default' };

function KVPairs({ data }) {
  const entries = Object.entries(data).filter(([, v]) => v !== null && v !== '');
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
      {entries.map(([k, v]) => (
        <Box key={k} sx={{ minWidth: 120 }}>
          <Typography variant="caption" color="text.secondary" display="block" fontWeight={600}>
            {k}
          </Typography>
          <Typography variant="body2" fontFamily="monospace">
            {String(v)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function SubjectDetailPanel({ subject }) {
  const [tab, setTab] = useState(0);
  const payload = subject.sdtm_payload || {};
  const dm = payload.DM || {};
  const ae = payload.AE || [];
  const lb = payload.LB || [];
  const vs = payload.VS || [];
  const ex = payload.EX || [];
  const ds = payload.DS || {};

  return (
    <Box sx={{ bgcolor: 'action.hover', px: 2, py: 1.5 }}>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 1.5, minHeight: 32, '& .MuiTab-root': { minHeight: 32, py: 0.5 } }}
      >
        <Tab label="Demographics" />
        <Tab label={ae.length > 0 ? `AE (${ae.length})` : 'AE'} />
        <Tab label={lb.length > 0 ? `Labs (${lb.length})` : 'Labs'} />
        <Tab label={vs.length > 0 ? `Vitals (${vs.length})` : 'Vitals'} />
        <Tab label={ex.length > 0 ? `Exposure (${ex.length})` : 'Exposure'} />
        <Tab label="Disposition" />
      </Tabs>

      {/* DM — Demographics */}
      {tab === 0 && <KVPairs data={dm} />}

      {/* AE — Adverse Events */}
      {tab === 1 && (
        ae.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No adverse events recorded.</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Term</TableCell>
                  <TableCell>System Organ Class</TableCell>
                  <TableCell>Severity</TableCell>
                  <TableCell>Serious</TableCell>
                  <TableCell>Relationship</TableCell>
                  <TableCell>Start</TableCell>
                  <TableCell>End</TableCell>
                  <TableCell>Outcome</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ae.map((row, i) => (
                  <TableRow key={i} hover>
                    <TableCell><Typography variant="body2" fontWeight={500}>{row.AETERM}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{row.AEBODSYS}</Typography></TableCell>
                    <TableCell>
                      <Chip
                        label={row.AESEV}
                        size="small"
                        color={AE_SEV_COLORS[row.AESEV] ?? 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{row.AESER}</TableCell>
                    <TableCell><Typography variant="caption">{row.AEREL}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{row.AESTDTC}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{row.AEENDTC}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{row.AEOUT}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      {/* LB — Lab Results */}
      {tab === 2 && (
        lb.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No lab results.</Typography>
        ) : (
          <TableContainer sx={{ maxHeight: 320 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Visit</TableCell>
                  <TableCell>Test</TableCell>
                  <TableCell align="right">Result</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Ref Range</TableCell>
                  <TableCell>Flag</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lb.map((row, i) => (
                  <TableRow
                    key={i}
                    hover
                    sx={row.LBNRIND !== 'NORMAL' ? {
                      bgcolor: row.LBNRIND === 'HIGH' ? 'rgba(211,47,47,0.06)' : 'rgba(237,108,2,0.06)',
                    } : {}}
                  >
                    <TableCell><Typography variant="caption">{row.VISIT}</Typography></TableCell>
                    <TableCell>{row.LBTEST}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontFamily="monospace">{row.LBORRES}</Typography>
                    </TableCell>
                    <TableCell><Typography variant="caption">{row.LBORRESU}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="caption">{row.LBNRLO}–{row.LBNRHI}</Typography>
                    </TableCell>
                    <TableCell>
                      {row.LBNRIND !== 'NORMAL' && (
                        <Chip
                          label={row.LBNRIND}
                          size="small"
                          color={row.LBNRIND === 'HIGH' ? 'error' : 'warning'}
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      {/* VS — Vital Signs */}
      {tab === 3 && (
        vs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No vital signs.</Typography>
        ) : (
          <TableContainer sx={{ maxHeight: 320 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Visit</TableCell>
                  <TableCell>Test</TableCell>
                  <TableCell align="right">Result</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vs.map((row, i) => (
                  <TableRow key={i} hover>
                    <TableCell><Typography variant="caption">{row.VISIT}</Typography></TableCell>
                    <TableCell>{row.VSTEST}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontFamily="monospace">{row.VSORRES}</Typography>
                    </TableCell>
                    <TableCell><Typography variant="caption">{row.VSORRESU}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{row.VSDTC}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      {/* EX — Exposure */}
      {tab === 4 && (
        ex.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No exposure data.</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Visit</TableCell>
                  <TableCell>Treatment</TableCell>
                  <TableCell align="right">Dose</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Route</TableCell>
                  <TableCell>Start Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ex.map((row, i) => (
                  <TableRow key={i} hover>
                    <TableCell><Typography variant="caption">{row.VISIT}</Typography></TableCell>
                    <TableCell>{row.EXTRT}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontFamily="monospace">{row.EXDOSE}</Typography>
                    </TableCell>
                    <TableCell><Typography variant="caption">{row.EXDOSU}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{row.EXROUTE}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{row.EXSTDTC}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      {/* DS — Disposition */}
      {tab === 5 && <KVPairs data={ds} />}
    </Box>
  );
}

export default function ClinicalTrials() {
  const dispatch = useDispatch();
  const { items, generating, generateError, lastProtocolId } = useSelector(
    (s) => s.user.trialSubjects
  );

  const [expanded, setExpanded] = useState(true);
  const [therapeuticArea, setTherapeuticArea] = useState('Oncology');
  const [phase, setPhase] = useState('Phase II');
  const [arms, setArms] = useState(DEFAULT_ARMS);
  const [count, setCount] = useState(20);
  const [primaryEndpoint, setPrimaryEndpoint] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [allocationModel, setAllocationModel] = useState('RANDOMIZED');
  const [interventionModel, setInterventionModel] = useState('PARALLEL');
  const [masking, setMasking] = useState('NONE');
  const [primaryPurpose, setPrimaryPurpose] = useState('TREATMENT');
  const [interventionType, setInterventionType] = useState('DRUG');
  const [sexEligibility, setSexEligibility] = useState('ALL');
  const [minimumAge, setMinimumAge] = useState('');
  const [maximumAge, setMaximumAge] = useState('');
  const [healthyVolunteers, setHealthyVolunteers] = useState(false);
  const [secondaryEndpoint, setSecondaryEndpoint] = useState('');

  const handleArmChange = (index, field, value) => {
    setArms((prev) => prev.map((a, i) => (i === index ? { ...a, [field]: value } : a)));
  };

  const addArm = () => setArms((prev) => [...prev, { arm_id: '', label: '', arm_type: 'EXPERIMENTAL' }]);

  const removeArm = (index) => setArms((prev) => prev.filter((_, i) => i !== index));

  const toggleRow = (id) =>
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleGenerate = async () => {
    const payload = {
      protocol: {
        therapeutic_area: therapeuticArea,
        phase,
        arms,
        visit_schedule: 'standard',
        primary_endpoint: primaryEndpoint,
        secondary_endpoint: secondaryEndpoint,
        allocation: allocationModel,
        intervention_model: interventionModel,
        masking,
        primary_purpose: primaryPurpose,
        intervention_type: interventionType,
        sex_eligibility: sexEligibility,
        minimum_age: minimumAge !== '' ? Number(minimumAge) : null,
        maximum_age: maximumAge !== '' ? Number(maximumAge) : null,
        healthy_volunteers: healthyVolunteers,
      },
      count,
    };
    const result = await dispatch(generateTrialSubjects(payload));
    if (!result.error) {
      setExpanded(false);
      setExpandedRows({});
    }
  };

  const handleExportCsv = async () => {
    if (!lastProtocolId) return;
    setExportLoading(true);
    setExportError(null);
    try {
      const apiUrl = config.getDynamicApiUrl();
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();
      const resp = await fetch(
        `${apiUrl}/api/v1/client/trial-subjects/export/?protocol_id=${lastProtocolId}&format=sdtm_csv`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) throw new Error(`Export failed: ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sdtm-export-${lastProtocolId}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(err.message);
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportJson = () => {
    if (items.length === 0) return;
    const blob = new Blob([JSON.stringify(items, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sdtm-export-${lastProtocolId ?? 'subjects'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const armsValid = arms.length > 0 && arms.every((a) => a.arm_id.trim() && a.label.trim());
  const minAgeNum = minimumAge !== '' ? Number(minimumAge) : null;
  const maxAgeNum = maximumAge !== '' ? Number(maximumAge) : null;
  const ageRangeValid =
    (minAgeNum === null || (!isNaN(minAgeNum) && minAgeNum >= 0)) &&
    (maxAgeNum === null || (!isNaN(maxAgeNum) && maxAgeNum >= 0)) &&
    (minAgeNum === null || maxAgeNum === null || minAgeNum < maxAgeNum);
  const canGenerate = armsValid && count >= 1 && count <= 500 && ageRangeValid && !generating;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        <ScienceIcon color="primary" />
        <Typography variant="h6" fontWeight={600}>
          Clinical Trials
        </Typography>
        <Chip label="Life Sciences" size="small" color="primary" variant="outlined" />
      </Stack>

      {/* Protocol configuration accordion */}
      <Accordion
        expanded={expanded}
        onChange={(_, v) => setExpanded(v)}
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          mb: 2,
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={500}>Configure Protocol</Typography>
          {!expanded && items.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              {therapeuticArea} · {phase} · {interventionModel} · {masking} masking · {items.length} subjects
            </Typography>
          )}
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2.5}>
            {/* Row 1: Therapeutic Area | Phase | Intervention Model | Primary Purpose */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Therapeutic Area</InputLabel>
                <Select
                  value={therapeuticArea}
                  label="Therapeutic Area"
                  onChange={(e) => setTherapeuticArea(e.target.value)}
                >
                  {THERAPEUTIC_AREAS.map((ta) => (
                    <MenuItem key={ta} value={ta}>{ta}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Phase</InputLabel>
                <Select value={phase} label="Phase" onChange={(e) => setPhase(e.target.value)}>
                  {PHASES.map((p) => (
                    <MenuItem key={p} value={p}>{p}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Intervention Model</InputLabel>
                <Select
                  value={interventionModel}
                  label="Intervention Model"
                  onChange={(e) => setInterventionModel(e.target.value)}
                >
                  {INTERVENTION_MODELS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Primary Purpose</InputLabel>
                <Select
                  value={primaryPurpose}
                  label="Primary Purpose"
                  onChange={(e) => setPrimaryPurpose(e.target.value)}
                >
                  {PRIMARY_PURPOSES.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            {/* Row 2: Allocation | Masking | Intervention Type | Subject Count */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Allocation</InputLabel>
                <Select
                  value={allocationModel}
                  label="Allocation"
                  onChange={(e) => setAllocationModel(e.target.value)}
                >
                  {ALLOCATIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Masking</InputLabel>
                <Select
                  value={masking}
                  label="Masking"
                  onChange={(e) => setMasking(e.target.value)}
                >
                  {MASKING_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Intervention Type</InputLabel>
                <Select
                  value={interventionType}
                  label="Intervention Type"
                  onChange={(e) => setInterventionType(e.target.value)}
                >
                  {INTERVENTION_TYPES.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Subject Count"
                type="number"
                size="small"
                value={count}
                onChange={(e) => setCount(Number(e.target.value))}
                inputProps={{ min: 1, max: 500 }}
                sx={{ minWidth: 160 }}
                helperText="1 – 500"
              />
            </Stack>

            {/* Trial Arms */}
            <Box>
              <Typography variant="body2" fontWeight={500} mb={1}>
                Trial Arms
              </Typography>
              <Stack spacing={1}>
                {arms.map((arm, i) => (
                  <Stack key={i} direction="row" spacing={1} alignItems="center">
                    <TextField
                      label="Arm ID"
                      size="small"
                      value={arm.arm_id}
                      onChange={(e) => handleArmChange(i, 'arm_id', e.target.value)}
                      sx={{ width: 120 }}
                      placeholder="TRT"
                    />
                    <TextField
                      label="Label"
                      size="small"
                      value={arm.label}
                      onChange={(e) => handleArmChange(i, 'label', e.target.value)}
                      sx={{ flex: 1 }}
                      placeholder="Treatment"
                    />
                    <FormControl size="small" sx={{ width: 180 }}>
                      <InputLabel>Arm Type</InputLabel>
                      <Select
                        value={arm.arm_type || 'EXPERIMENTAL'}
                        label="Arm Type"
                        onChange={(e) => handleArmChange(i, 'arm_type', e.target.value)}
                      >
                        {ARM_TYPES.map((o) => (
                          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <IconButton
                      size="small"
                      onClick={() => removeArm(i)}
                      disabled={arms.length === 1}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
                <Button
                  startIcon={<AddIcon />}
                  size="small"
                  onClick={addArm}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Add Arm
                </Button>
              </Stack>
            </Box>

            {/* Eligibility Criteria */}
            <Box>
              <Typography variant="body2" fontWeight={500} mb={1}>
                Eligibility Criteria
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Sex Eligibility</InputLabel>
                  <Select
                    value={sexEligibility}
                    label="Sex Eligibility"
                    onChange={(e) => setSexEligibility(e.target.value)}
                  >
                    {SEX_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Min Age"
                  type="number"
                  size="small"
                  value={minimumAge}
                  onChange={(e) => setMinimumAge(e.target.value)}
                  inputProps={{ min: 0 }}
                  placeholder="No min"
                  sx={{ width: 110 }}
                />

                <TextField
                  label="Max Age"
                  type="number"
                  size="small"
                  value={maximumAge}
                  onChange={(e) => setMaximumAge(e.target.value)}
                  inputProps={{ min: 0 }}
                  placeholder="No max"
                  sx={{ width: 110 }}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={healthyVolunteers}
                      onChange={(e) => setHealthyVolunteers(e.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Healthy Volunteers</Typography>}
                />
              </Stack>
            </Box>

            {/* Endpoints */}
            <Box>
              <Typography variant="body2" fontWeight={500} mb={1}>
                Endpoints
              </Typography>
              <Stack spacing={1.5}>
                <TextField
                  label="Primary Endpoint"
                  size="small"
                  value={primaryEndpoint}
                  onChange={(e) => setPrimaryEndpoint(e.target.value)}
                  placeholder="e.g. Overall Survival at 12 months"
                  fullWidth
                />
                <TextField
                  label="Secondary Endpoint"
                  size="small"
                  value={secondaryEndpoint}
                  onChange={(e) => setSecondaryEndpoint(e.target.value)}
                  placeholder="e.g. Progression-Free Survival at 6 months"
                  fullWidth
                />
              </Stack>
            </Box>

            {generateError && (
              <Alert severity="error" onClose={() => {}}>
                {typeof generateError === 'string' ? generateError : JSON.stringify(generateError)}
              </Alert>
            )}

            <Box>
              <Button
                variant="contained"
                onClick={handleGenerate}
                disabled={!canGenerate}
                startIcon={generating ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {generating ? 'Generating…' : 'Generate Subjects'}
              </Button>
            </Box>
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Results table */}
      {items.length > 0 && (
        <Box>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Typography variant="subtitle2" color="text.secondary">
                {items.length} subjects generated
              </Typography>
              <Typography variant="caption" color="text.secondary">
                · click a row to inspect SDTM data
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              {exportError && (
                <Typography variant="caption" color="error">{exportError}</Typography>
              )}
              <Tooltip title={!lastProtocolId ? 'Protocol ID unavailable — regenerate to enable export' : ''}>
                <span>
                  <ButtonGroup size="small" disabled={!lastProtocolId || exportLoading}>
                    <Button
                      startIcon={exportLoading ? <CircularProgress size={14} /> : <DownloadIcon />}
                      onClick={handleExportCsv}
                    >
                      SDTM CSV
                    </Button>
                    <Button onClick={handleExportJson}>JSON</Button>
                  </ButtonGroup>
                </span>
              </Tooltip>
            </Stack>
          </Stack>

          <Divider sx={{ mb: 1.5 }} />

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40, pr: 0 }} />
                  <TableCell>USUBJID</TableCell>
                  <TableCell>Arm</TableCell>
                  <TableCell align="center">Age</TableCell>
                  <TableCell align="center">Sex</TableCell>
                  <TableCell>Disposition</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((s) => (
                  <React.Fragment key={s.id}>
                    <TableRow
                      hover
                      onClick={() => toggleRow(s.id)}
                      sx={{ cursor: 'pointer', '& > *': { borderBottom: expandedRows[s.id] ? 'none' : undefined } }}
                    >
                      <TableCell sx={{ pr: 0, width: 40 }}>
                        <IconButton size="small" tabIndex={-1}>
                          {expandedRows[s.id]
                            ? <KeyboardArrowDownIcon fontSize="small" />
                            : <KeyboardArrowRightIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {s.usubjid}
                        </Typography>
                      </TableCell>
                      <TableCell>{s.arm_id}</TableCell>
                      <TableCell align="center">{s.age}</TableCell>
                      <TableCell align="center">{s.sex}</TableCell>
                      <TableCell>
                        <Chip
                          label={s.disposition}
                          size="small"
                          color={DISPOSITION_COLORS[s.disposition] ?? 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{ p: 0, border: expandedRows[s.id] ? undefined : 'none' }}
                      >
                        <Collapse in={!!expandedRows[s.id]} unmountOnExit>
                          <SubjectDetailPanel subject={s} />
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
}
